import React from 'react';

import { ApiOrder } from 'api/types';
import RemoveIcon from 'components/basic/icons/RemoveIcon';
import RouterLink from 'components/basic/Link/RouterLink';
import StopEventPropagationDiv from 'components/basic/panels/StopEventPropagationDiv';
import BasicTableStackable from 'components/basic/table/BasicTableStackable';
import {
  EXPAND_COLUMN_WIDTH,
  ExpandColumnIcon,
} from 'components/basic/table/components/ExpandColumnIcon';
import TextColored from 'components/basic/text/TextColored';
import { useSelectorActiveChain } from 'components/hooks';
import TPSLOrderColumnWrap from 'components/positions/takeProfitStopLoss/TPSLOrderColumnWrap';
import { TableCoinIcon } from 'config/coinIcons/CoinIcon';
import { formatDateTime } from 'lib/date';
import { formatPrice } from 'lib/display/formatting';
import { capitalizeFirstLetter } from 'lib/string';
import { getDashboardUrl } from 'reduxState/markets/listed/listedMarketsUtils';
import { screenSizes } from 'reduxState/screen/screenUtils';
import { useSelector } from 'reduxState/store';

import { useCancelOrder } from './cancelOrder';
import {
  getOrderQuantityWithSymbol,
  getOrderTotalInQuoteWithSymbol,
  getOrderTriggerExplanation,
  isOrderPostOnly,
  getOrderExecutedQuantityWithSymbol,
  getReadableOrderType,
} from './orderUtil';

type Props = {
  orders: ApiOrder[];
  onSelectOrder: (orderId: string) => void;
  isLoading: boolean;
  onCancelAll: () => void;
};

export default function ActiveOrdersTable({
  orders,
  isLoading,
  onSelectOrder,
  onCancelAll,
}: Props) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const isTablet = useSelector(state => state.screen.isTablet);
  const resolutionIndex = useSelector(state => state.screen.resolutionIndex);
  const isLargerMobile = resolutionIndex > screenSizes.micro && isMobile;

  const wallet = useSelector(state => state.wallet);
  const { walletAddress } = wallet;
  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);
  const { activeChain } = useSelectorActiveChain();

  const { cancelingOrderId, handleCancelOrder, renderCancelation } =
    useCancelOrder();

  const maximumPositionSizes = useSelector(
    state => state.markets.listedMarkets.maximumPositionSizes
  );

  return (
    <>
      {renderCancelation()}
      <BasicTableStackable
        notFixedLayout
        firstColumnPaddingLeft="16px"
        getItemKey={item => item.orderId}
        items={orders}
        stripes
        errorText={
          !walletAddress
            ? 'Connect wallet to see your orders'
            : isLoading
            ? undefined
            : orders.length === 0
            ? 'No active orders'
            : undefined
        }
        rowCustomizer={item => ({
          selected: cancelingOrderId
            ? item.orderId === cancelingOrderId
            : false,
        })}
        marginBottom="24px"
        onRowClick={
          isMobile ? order => onSelectOrder(order.orderId) : undefined
        }
        columns={orderOrUndefined => {
          const tickSizeDecimals =
            tickSizes?.[orderOrUndefined?.market || '']?.visibleDecimals;
          const stepSizeDecimals =
            stepSizes?.[orderOrUndefined?.market || '']?.visibleDecimals;
          const maximumPositionSize = orderOrUndefined
            ? maximumPositionSizes[orderOrUndefined.market]
            : undefined;
          return [
            {
              name: 'Date',
              render: isMobile ? false : order => formatDateTime(order.time),
            },
            {
              name: 'Market',
              fontFamily: 'standard',
              render: order =>
                isMobile ? (
                  <>
                    <TableCoinIcon symbol={order.baseSymbol} />
                    {order.market}
                  </>
                ) : (
                  <StopEventPropagationDiv style={{ display: 'inline-block' }}>
                    <RouterLink
                      to={getDashboardUrl(activeChain, order.market)}
                      title="Open market"
                      color="secondary"
                    >
                      <TableCoinIcon symbol={order.baseSymbol} />
                      {order.market}
                    </RouterLink>
                  </StopEventPropagationDiv>
                ),
            },
            {
              name: 'Type',
              render: order => getReadableOrderType(order.type),
              fontFamily: 'standard',
              stackWithPrevious: isMobile,
            },
            {
              name: 'Side',
              align: isMobile ? 'right' : 'left',
              fontFamily: 'standard',
              render: order => (
                <TextColored color={order.side === 'buy' ? 'green' : 'red'}>
                  {capitalizeFirstLetter(order.side)}
                </TextColored>
              ),
            },
            {
              name: 'Price',
              render: order =>
                order.price ? formatPrice(order.price, tickSizeDecimals) : '-',
              align: isMobile ? 'right' : 'left',
              stackWithPrevious: isMobile,
            },
            {
              name: 'Value',
              align: 'right',
              render: isLargerMobile
                ? order =>
                    getOrderTotalInQuoteWithSymbol(order, maximumPositionSize)
                : false,
            },
            {
              name: 'Quantity',
              align: isMobile ? 'right' : 'left',
              render: order =>
                getOrderQuantityWithSymbol(
                  order,
                  stepSizeDecimals,
                  maximumPositionSize
                ) || '',
            },
            {
              name: 'Value',
              render: isMobile
                ? false
                : order => getOrderTotalInQuoteWithSymbol(order, false) || '-',
            },
            {
              name: 'Filled',
              render: order =>
                getOrderExecutedQuantityWithSymbol(order, stepSizeDecimals),
              align: isMobile ? 'right' : 'left',
              stackWithPrevious: isMobile,
            },
            {
              name: 'Reduce-Only',
              fontFamily: 'standard',
              render: isMobile
                ? false
                : order => (order.reduceOnly ? 'Yes' : 'No'),
            },
            {
              name: 'Post-Only',
              fontFamily: 'standard',
              render:
                isMobile || isTablet
                  ? false
                  : order => (isOrderPostOnly(order) ? 'Yes' : 'No'),
            },
            {
              name: 'Trigger',
              render: isMobile
                ? false
                : order => getOrderTriggerExplanation(order, tickSizeDecimals),
            },
            {
              name: 'TP/SL',
              render: isMobile
                ? false
                : order => <TPSLOrderColumnWrap order={order} />,
            },
            {
              name: 'Cancel All',
              align: 'center',
              nameAsLink: true,
              disabled: !orders.length,
              onClick: onCancelAll,
              render: isMobile
                ? false
                : order => (
                    <RemoveIcon
                      onClick={
                        cancelingOrderId !== order.orderId
                          ? () => handleCancelOrder(order.orderId)
                          : undefined
                      }
                    />
                  ),
            },
            {
              name: 'Arrow',
              hideName: true,
              align: 'center',
              width: EXPAND_COLUMN_WIDTH,
              onValueClick:
                isMobile || !orderOrUndefined
                  ? undefined
                  : () => onSelectOrder(orderOrUndefined.orderId),
              render: isMobile ? false : <ExpandColumnIcon />,
            },
          ];
        }}
      />
    </>
  );
}
